/**
 * AI Chord Progression Generator
 * AI-powered chord progression generation using GPT-5.2
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Music2,
  Sparkles,
  Play,
  Pause,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  X,
  Loader2,
  Wand2,
  Download,
  Lightbulb,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options with emojis
const GENRES = [
  { id: 'pop', name: 'Pop', emoji: '🎵' },
  { id: 'rock', name: 'Rock', emoji: '🎸' },
  { id: 'jazz', name: 'Jazz', emoji: '🎷' },
  { id: 'electronic', name: 'Electronic', emoji: '🎧' },
  { id: 'rnb', name: 'R&B', emoji: '💜' },
  { id: 'hiphop', name: 'Hip-Hop', emoji: '🎤' },
  { id: 'lofi', name: 'Lo-Fi', emoji: '🌙' },
  { id: 'classical', name: 'Classical', emoji: '🎻' },
];

// Mood options
const MOODS = [
  { id: 'happy', name: 'Happy', emoji: '😊' },
  { id: 'sad', name: 'Sad', emoji: '😢' },
  { id: 'energetic', name: 'Energetic', emoji: '⚡' },
  { id: 'chill', name: 'Chill', emoji: '😌' },
  { id: 'dark', name: 'Dark', emoji: '🌑' },
  { id: 'uplifting', name: 'Uplifting', emoji: '🌟' },
  { id: 'romantic', name: 'Romantic', emoji: '💕' },
  { id: 'mysterious', name: 'Mysterious', emoji: '🔮' },
];

// Musical keys
const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Scales
const SCALES = [
  { id: 'major', name: 'Major' },
  { id: 'minor', name: 'Minor' },
  { id: 'dorian', name: 'Dorian' },
  { id: 'mixolydian', name: 'Mixolydian' },
];

// Complexity levels
const COMPLEXITY = [
  { id: 'simple', name: 'Simple', desc: 'Basic triads' },
  { id: 'moderate', name: 'Moderate', desc: '7th chords' },
  { id: 'complex', name: 'Complex', desc: 'Extensions' },
];

// Chord card component
const ChordCard = ({ chord, index, isPlaying, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className={`flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all ${
      isPlaying
        ? 'bg-violet-500/20 border-violet-500 ring-2 ring-violet-500/30'
        : 'bg-zinc-800/50 border-zinc-700 hover:border-violet-500/50'
    }`}
    onClick={onClick}
  >
    <span className="text-xs text-zinc-500 mb-1">{chord.roman_numeral || `${index + 1}`}</span>
    <span className="text-lg font-bold text-white">{chord.chord}</span>
    <span className="text-xs text-zinc-400 mt-1">{chord.function || chord.type}</span>
  </motion.div>
);

// Mini piano visualization
const MiniPiano = ({ notes = [], activeNotes = [] }) => {
  const startNote = 48; // C3
  const keyCount = 25;
  
  return (
    <div className="flex items-end justify-center h-12 gap-px">
      {Array.from({ length: keyCount }, (_, i) => {
        const midiNote = startNote + i;
        const noteInOctave = midiNote % 12;
        const isBlack = [1, 3, 6, 8, 10].includes(noteInOctave);
        const isInChord = notes.includes(midiNote);
        const isActive = activeNotes.includes(midiNote);
        
        return (
          <div
            key={midiNote}
            className={`transition-all duration-100 ${
              isBlack
                ? `w-2 h-7 -mx-1 z-10 rounded-b ${
                    isActive || isInChord
                      ? 'bg-violet-500 shadow-lg shadow-violet-500/50'
                      : 'bg-zinc-700'
                  }`
                : `w-3 h-10 rounded-b border-x border-b border-zinc-600 ${
                    isActive || isInChord
                      ? 'bg-violet-400 shadow-lg shadow-violet-400/50'
                      : 'bg-zinc-200'
                  }`
            }`}
          />
        );
      })}
    </div>
  );
};

export const AIChordGenerator = ({
  isOpen,
  onClose,
  onInsertProgression, // Callback to insert progression into DAW
  audioContext,
}) => {
  // State
  const [genre, setGenre] = useState('pop');
  const [mood, setMood] = useState('happy');
  const [musicalKey, setMusicalKey] = useState('C');
  const [scale, setScale] = useState('major');
  const [complexity, setComplexity] = useState('moderate');
  const [length, setLength] = useState(4);
  const [context, setContext] = useState('');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [progression, setProgression] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChordIndex, setCurrentChordIndex] = useState(-1);
  const [copied, setCopied] = useState(false);
  
  const playbackRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    if (audioContext && !gainRef.current) {
      gainRef.current = audioContext.createGain();
      gainRef.current.connect(audioContext.destination);
      gainRef.current.gain.value = 0.3;
    }
  }, [audioContext]);

  // Generate progression
  const generateProgression = async () => {
    setIsGenerating(true);
    setProgression(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/chord-progression`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genre,
          mood,
          key: musicalKey,
          scale,
          complexity,
          length,
          context: context || null,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setProgression(data.progression);
      toast.success('Chord progression generated!');
    } catch (err) {
      console.error('Error generating progression:', err);
      toast.error(err.message || 'Failed to generate progression');
    } finally {
      setIsGenerating(false);
    }
  };

  // Play a single chord
  const playChord = useCallback((midiNotes) => {
    if (!audioContext || !gainRef.current) return;
    
    // Stop any existing notes
    oscillatorsRef.current.forEach(osc => {
      try { osc.stop(); } catch (e) {}
    });
    oscillatorsRef.current = [];
    
    // Create oscillators for each note
    midiNotes.forEach(note => {
      const osc = audioContext.createOscillator();
      const noteGain = audioContext.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = 440 * Math.pow(2, (note - 69) / 12);
      
      noteGain.gain.value = 0;
      noteGain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.02);
      noteGain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
      
      osc.connect(noteGain);
      noteGain.connect(gainRef.current);
      
      osc.start();
      oscillatorsRef.current.push(osc);
    });
  }, [audioContext]);

  // Stop all notes
  const stopAllNotes = useCallback(() => {
    oscillatorsRef.current.forEach(osc => {
      try { osc.stop(); } catch (e) {}
    });
    oscillatorsRef.current = [];
  }, []);

  // Play progression
  const playProgression = useCallback(() => {
    if (!progression?.progression?.length) return;
    
    setIsPlaying(true);
    setCurrentChordIndex(0);
    
    const chords = progression.progression;
    let index = 0;
    
    const playNext = () => {
      if (index >= chords.length) {
        setIsPlaying(false);
        setCurrentChordIndex(-1);
        stopAllNotes();
        return;
      }
      
      const chord = chords[index];
      setCurrentChordIndex(index);
      
      if (chord.midi_notes) {
        playChord(chord.midi_notes);
      }
      
      index++;
      playbackRef.current = setTimeout(playNext, 800); // 800ms per chord
    };
    
    playNext();
  }, [progression, playChord, stopAllNotes]);

  // Stop playback
  const stopPlayback = useCallback(() => {
    if (playbackRef.current) {
      clearTimeout(playbackRef.current);
    }
    setIsPlaying(false);
    setCurrentChordIndex(-1);
    stopAllNotes();
  }, [stopAllNotes]);

  // Copy progression text
  const copyProgression = () => {
    if (!progression?.progression) return;
    
    const text = progression.progression.map(c => c.chord).join(' - ');
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Insert into DAW
  const insertIntoDaw = () => {
    if (!progression?.progression) return;
    
    onInsertProgression?.(progression.progression, {
      key: musicalKey,
      scale,
      genre,
      mood,
    });
    
    toast.success('Inserted into track!');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="ai-chord-generator"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Music2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">AI Chord Generator</h2>
                <p className="text-xs text-zinc-400">Powered by GPT-5.2</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
            {/* Settings Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Genre */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Genre</label>
                <div className="grid grid-cols-4 gap-1">
                  {GENRES.map(g => (
                    <button
                      key={g.id}
                      onClick={() => setGenre(g.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        genre === g.id
                          ? 'bg-violet-500/20 border border-violet-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={g.name}
                    >
                      <span className="text-lg">{g.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {GENRES.find(g => g.id === genre)?.name}</p>
              </div>
              
              {/* Mood */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Mood</label>
                <div className="grid grid-cols-4 gap-1">
                  {MOODS.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMood(m.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        mood === m.id
                          ? 'bg-violet-500/20 border border-violet-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={m.name}
                    >
                      <span className="text-lg">{m.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {MOODS.find(m => m.id === mood)?.name}</p>
              </div>
            </div>
            
            {/* Key & Scale Row */}
            <div className="grid grid-cols-3 gap-4">
              {/* Key */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Key</label>
                <div className="flex flex-wrap gap-1">
                  {KEYS.map(k => (
                    <button
                      key={k}
                      onClick={() => setMusicalKey(k)}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        musicalKey === k
                          ? 'bg-violet-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Scale */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Scale</label>
                <div className="flex flex-wrap gap-1">
                  {SCALES.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setScale(s.id)}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        scale === s.id
                          ? 'bg-violet-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Complexity */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Complexity</label>
                <div className="flex gap-1">
                  {COMPLEXITY.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setComplexity(c.id)}
                      className={`px-2 py-1 rounded text-xs flex-1 transition-all ${
                        complexity === c.id
                          ? 'bg-violet-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                      title={c.desc}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Length Selection */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-zinc-300">Length:</label>
              <div className="flex gap-2">
                {[4, 8, 16].map(l => (
                  <button
                    key={l}
                    onClick={() => setLength(l)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      length === l
                        ? 'bg-violet-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {l} chords
                  </button>
                ))}
              </div>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={generateProgression}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 h-12"
              data-testid="generate-chords-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Progression
                </>
              )}
            </Button>
            
            {/* Results */}
            {progression && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Chord Cards */}
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-white">
                      {musicalKey} {scale} - {GENRES.find(g => g.id === genre)?.name} {MOODS.find(m => m.id === mood)?.name}
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyProgression}
                        className="text-zinc-400 hover:text-white"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={isPlaying ? stopPlayback : playProgression}
                        className="text-zinc-400 hover:text-white"
                      >
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Chord Grid */}
                  <div className="grid grid-cols-4 gap-2">
                    {progression.progression?.map((chord, i) => (
                      <ChordCard
                        key={i}
                        chord={chord}
                        index={i}
                        isPlaying={currentChordIndex === i}
                        onClick={() => chord.midi_notes && playChord(chord.midi_notes)}
                      />
                    ))}
                  </div>
                  
                  {/* Piano Preview */}
                  {progression.progression?.[currentChordIndex]?.midi_notes && (
                    <div className="mt-4 p-3 bg-zinc-900/50 rounded-lg">
                      <MiniPiano
                        notes={progression.progression[currentChordIndex].midi_notes}
                        activeNotes={progression.progression[currentChordIndex].midi_notes}
                      />
                    </div>
                  )}
                </div>
                
                {/* Theory & Tips */}
                {(progression.theory_explanation || progression.genre_notes) && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-violet-400 mt-0.5" />
                      <div className="space-y-2">
                        {progression.theory_explanation && (
                          <p className="text-sm text-zinc-300">{progression.theory_explanation}</p>
                        )}
                        {progression.genre_notes && (
                          <p className="text-sm text-zinc-400">{progression.genre_notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Variations */}
                {progression.variations?.length > 0 && (
                  <div className="p-4 bg-zinc-800/30 rounded-xl">
                    <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-violet-400" />
                      Suggested Variations
                    </h4>
                    <div className="space-y-2">
                      {progression.variations.map((v, i) => (
                        <div key={i} className="p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-sm font-medium text-violet-400">{v.name}</p>
                          <p className="text-xs text-zinc-400">{v.description}</p>
                          {v.modified_chords && (
                            <p className="text-xs text-zinc-500 mt-1">{v.modified_chords.join(' - ')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={generateProgression}
                    variant="outline"
                    className="flex-1 border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={insertIntoDaw}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Insert into Track
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIChordGenerator;
